<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Navigation Header Component -->
    <NavHeader
      :dealerName="
        accountData && accountData[0]?.name ? accountData[0].name : dealerName
      "
    />

    <!-- Background Image with multiply blend effect -->
    <div class="fixed inset-0 -z-30">
      <img
        src="/assets/photo.jpg"
        alt="Background"
        class="w-full h-full object-cover opacity-50 bg-image-animate"
      />
    </div>

    <!-- Primary multiply blend overlay for rich color interaction -->
    <div
      class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"
    ></div>

    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/40 backdrop-blur-[2px]"></div>

    <!-- Loader -->
    <div
      v-if="isLoading"
      class="isLoading flex justify-center items-center min-h-screen"
    >
      <div role="status">
        <svg
          aria-hidden="true"
          class="w-14 h-14 text-gray-600 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Page Header -->
    <div class="relative z-1 pt-8 pb-2">
      <div class="max-w-[48rem] px-4 sm:px-6 lg:px-8 mx-auto">
        <div class="text-center mb-4">
          <!-- Back Button -->
          <div class="mb-4 flex justify-center">
            <router-link
              :to="`/account/${$route.params.slug}/dashboard`"
              class="inline-flex items-center px-4 py-2 bg-black/60 backdrop-blur-lg border border-white/20 rounded-xl text-white hover:bg-black/70 transition-all duration-200"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
              Back to Dashboard
            </router-link>
          </div>
          
          <div class="mb-3 inline-block px-4 py-1">
            <p class="text-blue-400 font-medium">Email Analytics</p>
          </div>
          <h1
            class="text-3xl md:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent"
          >
            Email Tracking
          </h1>
          <p class="text-gray-300 text-lg mb-3">
            Monitor delivery and engagement for welcome emails
          </p>
          <div class="flex sm:inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-5 py-3 bg-blue-500/20 border border-blue-400/50 rounded-full w-full sm:w-auto max-w-md sm:max-w-none mx-auto">
            <svg class="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm sm:text-base text-blue-400 font-semibold whitespace-nowrap">{{ formattedDateRange }}</span>
            <span class="hidden sm:inline text-blue-400/60">•</span>
            <span class="text-xs sm:text-sm text-blue-400/80 whitespace-nowrap">Last 30 Days</span>
          </div>
        </div>

      </div>
    </div>


    <!-- Email Tracking Component -->
    <div
      v-if="chartData && chartData.total_count"
      class="max-w-[48rem] px-4 pt-2 pb-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <EmailTrackingComponent
        :accountSlug="$route.params.slug"
        :submissions="chartData.entries"
        :formId="accountData && accountData[0]?.form"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!isLoading"
      class="max-w-[48rem] px-4 pt-2 pb-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-12 text-center shadow-2xl">
        <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
        <h3 class="text-xl font-bold text-white mb-2">No Data Available</h3>
        <p class="text-gray-300">
          No email submissions found for the selected date range.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import NavHeader from "@/components/NavHeader.vue";
import EmailTrackingComponent from "@/components/EmailTracking.vue";
import axios from "axios";
import { mapState } from "vuex";

export default {
  name: "EmailTrackingView",
  components: {
    NavHeader,
    EmailTrackingComponent
  },
  data() {
    return {
      isLoading: true,
      chartData: null
    };
  },
  computed: {
    ...mapState(['accountData']),
    dealerName() {
      // Get dealer name from slug - convert slug format to display format
      const slug = this.$route.params.slug;
      if (slug) {
        // Convert slug like "courtney-patterson-ford" to "Courtney Patterson Ford"
        return slug
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
      return "Dealership";
    },
    formattedDateRange() {
      // Calculate the date range (last 30 days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      };
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
      async fetchData() {
        this.isLoading = true;
        try {
          // Ensure account data is loaded in Vuex store first
          if (!this.accountData || !this.accountData[0]) {
            await this.$store.dispatch('fetchAccountData', this.$route.params.slug);
          }
          
          // Fetch all form submissions (accountData comes from Vuex store)
          await this.fetchChartData();
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          this.isLoading = false;
        }
      },

    async fetchChartData() {
      try {
        // Get form ID from Vuex store (populated by store's fetchAccountData action)
        const formId = this.accountData && this.accountData[0]?.form;
        const slug = this.$route.params.slug;

        if (!formId) {
          console.error('No form ID configured for this account. Make sure account data is loaded in store.');
          this.chartData = { total_count: 0, entries: [] };
          return;
        }
        
        console.log(`Using form ID ${formId} for account ${slug}`);

        // Calculate date range - last 30 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30); // 30 days back
        
        const formatDate = (date) => {
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        const searchCriteria = {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate)
        };

        console.log(`Fetching entries from ${formatDate(startDate)} to ${formatDate(endDate)}`);

        // Fetch from Gravity Forms API with date filter
        const GF_CONSUMER_KEY = 'ck_9eb5fc380da548b29cd454ad4e4e40edbf6f8aad';
        const GF_CONSUMER_SECRET = 'cs_15706f39bc2a94c26ad71e695a2e730e29f6a7ae';

        const response = await axios.get(
          `https://engagr.driveagent.io/wp-json/gf/v2/forms/${formId}/entries`,
          {
            params: {
              consumer_key: GF_CONSUMER_KEY,
              consumer_secret: GF_CONSUMER_SECRET,
              search: JSON.stringify(searchCriteria),
              paging: { page_size: 1000 }
            }
          }
        );

        // Transform Gravity Forms response to match expected structure
        this.chartData = {
          total_count: response.data.total_count || 0,
          entries: response.data.entries || []
        };

        console.log(`Fetched ${this.chartData.total_count} submissions for form ${formId}`);
      } catch (error) {
        console.error("Error fetching chart data:", error);
        this.chartData = { total_count: 0, entries: [] };
      }
    }
  }
};
</script>

<style scoped>
/* Animation for background image */
@keyframes backgroundAnimation {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.bg-image-animate {
  animation: backgroundAnimation 30s ease-in-out infinite;
}
</style>

